import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Center,
  Image,
  SimpleGrid,
  FileButton,
  Box,
  Tabs,
  Loader,
  Modal,
  Badge
} from '@mantine/core';
import { IconArrowLeft, IconCamera, IconPlus, IconCheck, IconSend } from '@tabler/icons-react';
import { useApiClient } from '../../api';
import { uploadProductImage } from '../../api/uploads';

// Parses a dollar amount typed by the user ("$50", "50.00") into integer cents.
function parsePriceCents(input) {
  const value = typeof input === 'number' ? input : parseFloat(String(input).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

const CreateProductPage = () => {
  const [activeTab, setActiveTab] = useState('pedicure');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [productComplete, setProductComplete] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: ''
  });
  // Each entry is { file, url } — the File for upload, the object URL for preview
  const [productImages, setProductImages] = useState([]);
  const navigate = useNavigate();
  const api = useApiClient();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (files) => {
    if (files) {
      const newImages = Array.from(files).map(file => ({ file, url: URL.createObjectURL(file) }));
      setProductImages(prev => [...prev, ...newImages].slice(0, 5));
    }
  };

  const handleRemoveImage = (index) => {
    setProductImages(prev => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const createProduct = async (status) => {
    setLoading(true);
    setError(null);

    const priceCents = parsePriceCents(formData.price);
    if (priceCents === null) {
      setError('Please enter a valid price.');
      setLoading(false);
      return false;
    }

    try {
      const category = activeTab === 'pedicure' ? 'pedicure' : formData.category;
      const productData = {
        title: formData.title,
        description: formData.description,
        price_cents: priceCents,
        tags: [activeTab, category].filter(Boolean),
        status
      };

      const product = await api.post('/api/v1/me/seller/products', productData);

      // Upload images after the product exists (the upload endpoint requires
      // an owned product_id). Failures here don't fail the whole creation.
      const failures = [];
      for (const img of productImages) {
        try {
          await uploadProductImage(api, img.file, product.id);
        } catch (err) {
          console.error('Error uploading product image:', err);
          failures.push(img.file.name);
        }
      }
      if (failures.length > 0) {
        setError(`Product created, but some images failed to upload: ${failures.join(', ')}`);
      }

      return true;
    } catch (err) {
      console.error('Error creating product:', err);
      setError(err.message || 'Failed to create product. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await createProduct('active');
    if (ok) {
      setProductComplete(true);
      setTimeout(() => {
        navigate('/seller/dashboard');
      }, 1500);
    }
  };

  const handleSaveDraft = async () => {
    const ok = await createProduct('draft');
    if (ok) {
      navigate('/seller/dashboard');
    }
  };

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Button
            component={Link}
            to="/seller/dashboard"
            variant="subtle"
            leftSection={<IconArrowLeft size="1rem" />}
          >
            Back to Dashboard
          </Button>
        </Group>

        <Center>
          <Stack gap="md" align="center" maw={400}>
            <Title order={1} ta="center">
              Create New Product
            </Title>
            <Text size="lg" ta="center" c="dimmed">
              List your products on PediPiggy
            </Text>
          </Stack>
        </Center>

        <Card shadow="md" padding="xl" radius="md" withBorder>
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List grow>
              <Tabs.Tab value="pedicure" leftSection={<IconPlus size={16} />}>
                List Your Pedicure
              </Tabs.Tab>
              <Tabs.Tab value="sell-others" leftSection={<IconPlus size={16} />}>
                Sell Others
              </Tabs.Tab>
            </Tabs.List>

            <form onSubmit={handleSubmit}>
              <Stack gap="xl" mt="xl">
                {/* Product Images */}
                <Stack gap="md">
                  <Text fw={500}>Product Photos (Optional)</Text>
                  <Text size="sm" c="dimmed">
                    Add up to 5 photos of your product
                  </Text>
                  <SimpleGrid cols={{ base: 3, sm: 5 }} spacing="sm">
                    {productImages.map((img, index) => (
                      <Box key={index} style={{ position: 'relative' }}>
                        <Image
                          src={img.url}
                          alt={`Product ${index + 1}`}
                          radius="md"
                          h={80}
                          style={{ objectFit: 'cover' }}
                        />
                        <Button
                          size="xs"
                          color="red"
                          variant="filled"
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            padding: 2,
                            minWidth: 20,
                            width: 20,
                            height: 20
                          }}
                          onClick={() => handleRemoveImage(index)}
                        >
                          ×
                        </Button>
                      </Box>
                    ))}
                    {productImages.length < 5 && (
                      <FileButton onChange={handleImageUpload} accept="image/*" multiple>
                        {(props) => (
                          <Box
                            {...props}
                            style={{
                              border: '2px dashed #ced4da',
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 80,
                              cursor: 'pointer',
                              backgroundColor: '#f8f9fa'
                            }}
                          >
                            <IconCamera size={24} color="#adb5bd" />
                          </Box>
                        )}
                      </FileButton>
                    )}
                  </SimpleGrid>
                </Stack>

                {/* Title */}
                {activeTab === 'pedicure' ? (
                  <TextInput
                    label="Service Title"
                    placeholder="e.g., Deluxe Spa Pedicure"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                ) : (
                  <TextInput
                    label="Product Title"
                    placeholder="What are you selling?"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                )}

                {/* Description */}
                <Textarea
                  label={activeTab === 'pedicure' ? "Service Description" : "Description"}
                  placeholder={activeTab === 'pedicure' ? "Describe your pedicure service..." : "Describe your product..."}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  minRows={3}
                  maxRows={5}
                  required
                />

                {/* Pedicure Price Input */}
                {activeTab === 'pedicure' && (
                  <TextInput
                    label="How much is your pedicure costing?"
                    placeholder="e.g., $50.00"
                    description="Set your pedicure service price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                  />
                )}

                {/* Category for Sell Others */}
                {activeTab === 'sell-others' && (
                  <Select
                    label="What are you selling?"
                    placeholder="Select category"
                    data={[
                      { value: 'toe-nail-clippings', label: 'Toe Nail Clippings' },
                      { value: 'socks', label: 'Socks' },
                      { value: 'pedicure-water', label: 'Pedicure Water' },
                      { value: 'other', label: 'Other' }
                    ]}
                    value={formData.category}
                    onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    required
                  />
                )}

                {/* Price for Sell Others */}
                {activeTab === 'sell-others' && (
                  <NumberInput
                    label="Price ($)"
                    placeholder="0.00"
                    prefix="$"
                    min={0}
                    step={0.01}
                    value={formData.price}
                    onChange={(value) => setFormData(prev => ({ ...prev, price: value }))}
                    required
                  />
                )}

                {/* Error Message */}
                {error && (
                  <Text c="red" ta="center">{error}</Text>
                )}

                {/* Action Buttons */}
                <Group grow>
                  <Button
                    variant="outline"
                    color="gray"
                    onClick={() => navigate('/seller/dashboard')}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="light"
                    color="grape"
                    onClick={() => setPreviewOpen(true)}
                    disabled={!formData.title || !formData.price}
                  >
                    Preview Post
                  </Button>
                </Group>
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  color="grape"
                  leftSection={<IconSend size={18} />}
                  disabled={!formData.title || !formData.price || loading}
                >
                  {loading ? <Loader size="sm" /> : 'Publish Post'}
                </Button>
              </Stack>
            </form>
          </Tabs>
        </Card>

        {/* Preview Modal */}
        <Modal
          opened={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title="Preview Your Post"
          size="md"
        >
          <Stack gap="md">
            <Group justify="space-between">
              <Badge color="grape">{activeTab === 'pedicure' ? 'Pedicure Service' : 'Product'}</Badge>
              <Text fw={700} size="xl" c="grape">${formData.price || '0.00'}</Text>
            </Group>

            <Title order={3}>{formData.title || 'No Title'}</Title>

            <Text c="dimmed">{formData.description || 'No description'}</Text>

            {productImages.length > 0 && (
              <SimpleGrid cols={3}>
                {productImages.slice(0, 3).map((img, idx) => (
                  <Image key={idx} src={img.url} radius="md" />
                ))}
              </SimpleGrid>
            )}

            <Group justify="center" mt="md">
              <Button variant="outline" color="gray" onClick={() => setPreviewOpen(false)}>
                Close Preview
              </Button>
              <Button variant="light" color="pink" loading={loading} onClick={async () => {
                setPreviewOpen(false);
                await handleSaveDraft();
              }}>
                Save for Later
              </Button>
              <Button color="grape" onClick={() => {
                setPreviewOpen(false);
                document.querySelector('form')?.requestSubmit();
              }}>
                Publish Now
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Success Message */}
        {productComplete && (
          <Card shadow="md" padding="xl" radius="md" withBorder>
            <Stack align="center" gap="md">
              <IconCheck size={48} color="var(--mantine-color-green-6)" />
              <Title order={2} ta="center">
                Product Listed!
              </Title>
              <Text ta="center" c="dimmed">
                Your product is now live on PediPiggy
              </Text>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};

export default CreateProductPage;
